/**
 * Debugging aid for freezes on DealConfigureScreen.
 *
 * Enable: URL `?dealConfigureDebug=1` or `localStorage.__DEAL_CONFIGURE_DEBUG = '1'` before navigation.
 *
 * Timeline: `localStorage.__dealFreezeDebug` (truncated). Clear with `clearDealFreezeDebugTrace()`.
 *
 * Writes are batched via `queueMicrotask` — hundreds of synchronous `setItem` per second would freeze
 * the main thread while debug is enabled.
 */
const LS_DEBUG_FLAG = '__DEAL_CONFIGURE_DEBUG';
const LS_TRACE_KEY = '__dealFreezeDebug';
const TRACE_MAX_CHARS = 20000;
const HEARTBEAT_MS = 120;
/** Summarize to storage every N ticks — avoid syncing localStorage every heartbeat. */
const HEARTBEAT_STORAGE_EVERY_TICKS = 10;
let pendingRows = [];
let flushScheduled = false;
function readUrlDebugFlag() {
    if (typeof window === 'undefined')
        return false;
    try {
        return (new URLSearchParams(window.location.search).get('dealConfigureDebug') ===
            '1');
    }
    catch (_a) {
        return false;
    }
}
function readLsDebugFlag() {
    try {
        return (typeof localStorage !== 'undefined' &&
            localStorage.getItem(LS_DEBUG_FLAG) === '1');
    }
    catch (_a) {
        return false;
    }
}
export function isDealFreezeDebugEnabled() {
    return readUrlDebugFlag() || readLsDebugFlag();
}
function persistChunk(chunk) {
    var _a;
    const prev = (_a = localStorage.getItem(LS_TRACE_KEY)) !== null && _a !== void 0 ? _a : '';
    localStorage.setItem(LS_TRACE_KEY, (prev + chunk).slice(-TRACE_MAX_CHARS));
}
function runFlush() {
    flushScheduled = false;
    if (!isDealFreezeDebugEnabled()) {
        pendingRows = [];
        return;
    }
    if (pendingRows.length === 0)
        return;
    try {
        persistChunk(pendingRows.join(''));
        pendingRows = [];
    }
    catch (_a) {
        pendingRows = [];
    }
}
function scheduleFlush() {
    if (flushScheduled)
        return;
    flushScheduled = true;
    queueMicrotask(runFlush);
}
/** Force pending lines onto disk e.g. before leaving the screen or tearing down heartbeat. */
export function flushDealFreezeDebug() {
    flushScheduled = false;
    if (!isDealFreezeDebugEnabled()) {
        pendingRows = [];
        return;
    }
    try {
        if (pendingRows.length > 0) {
            persistChunk(pendingRows.join(''));
            pendingRows = [];
        }
    }
    catch (_a) {
        pendingRows = [];
    }
}
export function appendDealFreezeDebug(message) {
    if (!isDealFreezeDebugEnabled())
        return;
    try {
        const elapsed = typeof performance !== 'undefined' && performance.now
            ? `${performance.now().toFixed(1)}ms`
            : `${Date.now()}`;
        pendingRows.push(`[${elapsed}]\t${message}\n`);
        scheduleFlush();
    }
    catch (_a) {
        // ignore quota / private mode
    }
}
/**
 * @returns Cleanup to stop heartbeats on unmount
 */
export function startDealFreezeHeartbeat() {
    if (typeof window === 'undefined' ||
        typeof window.setInterval !== 'function') {
        return undefined;
    }
    if (!isDealFreezeDebugEnabled())
        return undefined;
    appendDealFreezeDebug('heartbeat.start');
    let n = 0;
    const id = window.setInterval(() => {
        n += 1;
        window.__dealConfigureFreezeHb = n;
        if (n % HEARTBEAT_STORAGE_EVERY_TICKS === 0) {
            appendDealFreezeDebug(`heartbeat.summary.${n}`);
        }
    }, HEARTBEAT_MS);
    return () => {
        window.clearInterval(id);
        delete window.__dealConfigureFreezeHb;
        appendDealFreezeDebug('heartbeat.stop');
        flushDealFreezeDebug();
    };
}
export function clearDealFreezeDebugTrace() {
    flushScheduled = false;
    pendingRows = [];
    try {
        localStorage.removeItem(LS_TRACE_KEY);
    }
    catch (_a) {
        // ignore
    }
}
